/**
 * S7 (30-Sep-2026 Stores doc) — Stores › Reports tab.
 * Five Excel-exportable reports (Stock as on Date, Consumption, Stock Ageing,
 * Stock Ledger, GRN Register) with per-report filters and an in-page preview.
 * Backend: /api/stores/reports/<id> (JSON) + /api/stores/reports/<id>/export (.xlsx).
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Download, Loader2, ChevronLeft, FileSpreadsheet, RefreshCw } from 'lucide-react';
import { apiGet, apiDownload } from '../../lib/api';

const N = { num: true };
const REPORTS = [
  {
    id: 'stock-as-on', title: 'Stock as on Date',
    blurb: 'Closing stock & value at any date, plus qty still awaiting QC.',
    filters: ['as_on', 'project', 'location', 'item_group'],
    cols: [
      { k: 'sno', l: 'S.No', ...N }, { k: 'project', l: 'Project Name' }, { k: 'site', l: 'Site' },
      { k: 'group', l: 'Item Group' }, { k: 'desc', l: 'Item Desc' }, { k: 'code', l: 'Item Code' },
      { k: 'unit', l: 'Unit' }, { k: 'closing_qty', l: 'Closing Stock Qty', ...N },
      { k: 'closing_value', l: 'Closing Stock Value', ...N, money: true }, { k: 'awaiting_qc', l: 'Awaiting QC Qty', ...N },
    ],
  },
  {
    id: 'consumption', title: 'Consumption',
    blurb: 'Opening → received → issued/transferred/returned → closing for a date range.',
    filters: ['date_from', 'date_to', 'project', 'location', 'item_group'],
    cols: [
      { k: 'project', l: 'Project Name' }, { k: 'group', l: 'Item Group' }, { k: 'desc', l: 'Item Desc' },
      { k: 'code', l: 'Item Code' }, { k: 'unit', l: 'Unit' },
      { k: 'opening_qty', l: 'Opening Qty (A)', ...N }, { k: 'opening_value', l: 'Opening Value', ...N, money: true },
      { k: 'received_qty', l: 'Received Qty (B)', ...N }, { k: 'received_value', l: 'Received Value', ...N, money: true },
      { k: 'total_qty', l: 'Total Qty (C)', ...N }, { k: 'issue_qty', l: 'Issue Qty (D)', ...N },
      { k: 'transfer_qty', l: 'Project Transfer Qty (E)', ...N }, { k: 'return_qty', l: 'Goods Return Qty (F)', ...N },
      { k: 'total_issued_qty', l: 'Total Issued Qty (G)', ...N }, { k: 'total_issued_value', l: 'Total Issued Value', ...N, money: true },
      { k: 'closing_qty', l: 'Closing Qty', ...N }, { k: 'closing_value', l: 'Closing Value', ...N, money: true },
      { k: 'awaiting_qc', l: 'Awaiting QC Qty', ...N },
    ],
  },
  {
    id: 'stock-ageing', title: 'Stock Ageing',
    blurb: 'On-hand stock bucketed 0–30 / 31–90 / 91–180 / >180 days from receipt date.',
    filters: ['as_on', 'project', 'location', 'item_group', 'item_desc', 'voucher_no'],
    cols: [
      { k: 'project', l: 'Project' }, { k: 'godown', l: 'Godown' }, { k: 'group', l: 'Item Group' },
      { k: 'desc', l: 'Item Desc' }, { k: 'code', l: 'Item Code' }, { k: 'unit', l: 'Unit' },
      { k: 'closing_qty', l: 'Closing Qty', ...N }, { k: 'closing_value', l: 'Closing Value', ...N, money: true },
      { k: 'q0_30', l: '0–30d Qty', ...N }, { k: 'v0_30', l: '0–30d Value', ...N, money: true },
      { k: 'q31_90', l: '31–90d Qty', ...N }, { k: 'v31_90', l: '31–90d Value', ...N, money: true },
      { k: 'q91_180', l: '91–180d Qty', ...N }, { k: 'v91_180', l: '91–180d Value', ...N, money: true },
      { k: 'q180p', l: '>180d Qty', ...N }, { k: 'v180p', l: '>180d Value', ...N, money: true },
    ],
  },
  {
    id: 'stock-ledger', title: 'Stock Ledger',
    blurb: 'Movement summary per material: opening, receipts, issues, transfers, returns, closing.',
    filters: ['date_from', 'date_to', 'project', 'location'],
    cols: [
      { k: 'project', l: 'Project' }, { k: 'group', l: 'Item Group' }, { k: 'desc', l: 'Item Desc' },
      { k: 'unit', l: 'Unit' }, { k: 'mfg_date', l: 'Manufacturing Date' },
      { k: 'opening_qty', l: 'Opening Qty', ...N }, { k: 'receipt_qty', l: 'Receipt Qty', ...N },
      { k: 'total_qty', l: 'Total Qty', ...N }, { k: 'issue_qty', l: 'Issue Qty', ...N },
      { k: 'transfer_issue_qty', l: 'Project Transfer Issue Qty', ...N }, { k: 'goods_return_qty', l: 'Goods Return Qty', ...N },
      { k: 'total_issued_qty', l: 'Total Issued Qty', ...N }, { k: 'closing_qty', l: 'Closing Qty', ...N },
      { k: 'received_amount', l: 'Received Amount', ...N, money: true }, { k: 'closing_value', l: 'Closing Stock Value', ...N, money: true },
      { k: 'awaiting_qc', l: 'Awaiting QC Qty', ...N },
    ],
  },
  {
    id: 'grn-register', title: 'GRN Register',
    blurb: 'Every GRN line with challan, vehicle, PO, balance-yet-to-receive and QC status.',
    filters: ['date_from', 'date_to', 'project', 'location', 'supplier', 'po_no', 'item_group', 'item_desc', 'include_cancelled'],
    cols: [
      { k: 'grn_no', l: 'GRN No' }, { k: 'grn_date', l: 'GRN Date' }, { k: 'project', l: 'Project' },
      { k: 'supplier', l: 'Supplier' }, { k: 'godown', l: 'Godown' }, { k: 'challan_no', l: 'Challan No' },
      { k: 'challan_date', l: 'Challan Date' }, { k: 'vehicle_no', l: 'Vehicle No' }, { k: 'po_no', l: 'PO No' },
      { k: 'group', l: 'Item Group' }, { k: 'desc', l: 'Item Desc' }, { k: 'brand', l: 'Item Brand' },
      { k: 'unit', l: 'Unit' }, { k: 'mfg_date', l: 'Manufacturing Date' }, { k: 'balance_qty', l: 'GRN Balance Qty', ...N },
      { k: 'challan_qty', l: 'Challan Qty', ...N }, { k: 'received_qty', l: 'Received Qty', ...N },
      { k: 'po_rate', l: 'PO Rate', ...N, money: true }, { k: 'total_amt', l: 'Total Amt', ...N, money: true },
      { k: 'status', l: 'Status' },
    ],
  },
];

const FILTER_LABELS = {
  as_on: 'As on date', date_from: 'From', date_to: 'To', project: 'Project',
  location: 'Godown / Location', item_group: 'Item Group', item_desc: 'Item Desc',
  voucher_no: 'Voucher No', supplier: 'Supplier', po_no: 'PO No',
  include_cancelled: 'Include cancelled GRNs',
};

const fmt = (v, money) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v !== 'number') return v;
  return v.toLocaleString('en-IN', money ? { maximumFractionDigits: 2 } : { maximumFractionDigits: 3 });
};

export function ReportsTab() {
  const [active, setActive] = useState(null);          // report id
  const [filters, setFilters] = useState({});
  const [projects, setProjects] = useState([]);
  const [offices, setOffices] = useState([]);
  const [groups, setGroups] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    apiGet('/projects').then(d => setProjects(Array.isArray(d) ? d : (d?.items || d?.projects || []))).catch(() => {});
    apiGet('/config-masters/office_locations').then(d => setOffices(d?.values || [])).catch(() => {});
    apiGet('/material-master/categories')
      .then(d => setGroups(Array.isArray(d) ? d : (d?.categories || d?.items || [])))
      .catch(() => {});
  }, []);

  const report = REPORTS.find(r => r.id === active);
  const locationOptions = useMemo(() => ([
    { v: '', l: 'All locations' },
    { v: 'godown', l: 'Central Godown' },
    ...offices.map(o => ({ v: `office:${o.id}`, l: o.name || o.id })),
    ...projects.map(p => ({ v: `project:${p.id}`, l: p.code ? `${p.code} · ${p.name}` : p.name })),
  ]), [offices, projects]);

  const params = () => {
    const p = {};
    for (const f of report.filters) {
      if (f === 'include_cancelled') {
        if (filters.include_cancelled) p[f] = 'true';
        continue;
      }
      const v = (filters[f] || '').trim();
      if (v) p[f] = v;
    }
    return p;
  };

  const runPreview = async () => {
    setLoading(true);
    try {
      const d = await apiGet(`/stores/reports/${report.id}`, params());
      setPreview(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not run report');
    } finally { setLoading(false); }
  };

  const runExport = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams(params()).toString();
      await apiDownload(`/stores/reports/${report.id}/export${qs ? `?${qs}` : ''}`, `${report.id}.xlsx`);
      toast.success('Excel downloaded');
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  if (!report) {
    return (
      <div className="space-y-4" data-testid="stores-reports-tab">
        <div className="font-display font-bold text-base text-slate-800">Stores Reports</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {REPORTS.map(r => (
            <button key={r.id} onClick={() => { setActive(r.id); setFilters({}); setPreview(null); }}
              className="text-left bg-white border border-slate-200 rounded-sm p-4 hover:border-[#3b4872] hover:shadow-sm transition-colors"
              data-testid={`report-card-${r.id}`}>
              <div className="flex items-center gap-2 font-display font-bold text-sm text-slate-800">
                <FileSpreadsheet size={15} className="text-[#3b4872]" /> {r.title}
              </div>
              <div className="text-xs text-slate-500 mt-1.5">{r.blurb}</div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const needsRange = report.filters.includes('date_from');
  const rangeMissing = needsRange && (!filters.date_from || !filters.date_to);

  return (
    <div className="space-y-4" data-testid={`report-view-${report.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => { setActive(null); setPreview(null); }}
          className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1" data-testid="report-back-btn">
          <ChevronLeft size={13} /> All reports
        </button>
        <span className="text-xs text-slate-300">|</span>
        <span className="font-display font-bold text-sm text-slate-800">{report.title}</span>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 flex flex-wrap items-end gap-3">
        {report.filters.includes('as_on') && (
          <label className="text-xs">
            <span className="block font-semibold text-slate-600 mb-1">{FILTER_LABELS.as_on}</span>
            <input type="date" value={filters.as_on || ''} onChange={e => set('as_on', e.target.value)}
              className="border border-slate-300 rounded px-2.5 py-1.5 text-xs focus:outline-none focus:border-[#3b4872]"
              data-testid="filter-as-on" />
          </label>
        )}
        {needsRange && (
          <>
            <label className="text-xs">
              <span className="block font-semibold text-slate-600 mb-1">{FILTER_LABELS.date_from}</span>
              <input type="date" value={filters.date_from || ''} onChange={e => set('date_from', e.target.value)}
                className="border border-slate-300 rounded px-2.5 py-1.5 text-xs focus:outline-none focus:border-[#3b4872]"
                data-testid="filter-date-from" />
            </label>
            <label className="text-xs">
              <span className="block font-semibold text-slate-600 mb-1">{FILTER_LABELS.date_to}</span>
              <input type="date" value={filters.date_to || ''} onChange={e => set('date_to', e.target.value)}
                className="border border-slate-300 rounded px-2.5 py-1.5 text-xs focus:outline-none focus:border-[#3b4872]"
                data-testid="filter-date-to" />
            </label>
          </>
        )}
        {report.filters.includes('project') && (
          <label className="text-xs">
            <span className="block font-semibold text-slate-600 mb-1">{FILTER_LABELS.project}</span>
            <select value={filters.project || ''} onChange={e => set('project', e.target.value)}
              className="border border-slate-300 rounded px-2.5 py-1.5 text-xs bg-white min-w-[170px] focus:outline-none focus:border-[#3b4872]"
              data-testid="filter-project">
              <option value="">All projects</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
            </select>
          </label>
        )}
        {report.filters.includes('location') && (
          <label className="text-xs">
            <span className="block font-semibold text-slate-600 mb-1">{FILTER_LABELS.location}</span>
            <select value={filters.location || ''} onChange={e => set('location', e.target.value)}
              className="border border-slate-300 rounded px-2.5 py-1.5 text-xs bg-white min-w-[150px] focus:outline-none focus:border-[#3b4872]"
              data-testid="filter-location">
              {locationOptions.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </label>
        )}
        {report.filters.includes('item_group') && (
          <label className="text-xs">
            <span className="block font-semibold text-slate-600 mb-1">{FILTER_LABELS.item_group}</span>
            <select value={filters.item_group || ''} onChange={e => set('item_group', e.target.value)}
              className="border border-slate-300 rounded px-2.5 py-1.5 text-xs bg-white min-w-[140px] focus:outline-none focus:border-[#3b4872]"
              data-testid="filter-item-group">
              <option value="">All groups</option>
              {groups.map(g => <option key={g.category} value={g.category}>{g.category}</option>)}
            </select>
          </label>
        )}
        {report.filters.includes('item_desc') && (
          <label className="text-xs">
            <span className="block font-semibold text-slate-600 mb-1">{FILTER_LABELS.item_desc}</span>
            <input value={filters.item_desc || ''} onChange={e => set('item_desc', e.target.value)}
              placeholder="Search description…"
              className="border border-slate-300 rounded px-2.5 py-1.5 text-xs w-40 focus:outline-none focus:border-[#3b4872]"
              data-testid="filter-item-desc" />
          </label>
        )}
        {report.filters.includes('voucher_no') && (
          <label className="text-xs">
            <span className="block font-semibold text-slate-600 mb-1">{FILTER_LABELS.voucher_no}</span>
            <input value={filters.voucher_no || ''} onChange={e => set('voucher_no', e.target.value)}
              placeholder="GRN / TRF no…"
              className="border border-slate-300 rounded px-2.5 py-1.5 text-xs w-36 focus:outline-none focus:border-[#3b4872]"
              data-testid="filter-voucher-no" />
          </label>
        )}
        {report.filters.includes('supplier') && (
          <label className="text-xs">
            <span className="block font-semibold text-slate-600 mb-1">{FILTER_LABELS.supplier}</span>
            <input value={filters.supplier || ''} onChange={e => set('supplier', e.target.value)}
              placeholder="Supplier name…"
              className="border border-slate-300 rounded px-2.5 py-1.5 text-xs w-40 focus:outline-none focus:border-[#3b4872]"
              data-testid="filter-supplier" />
          </label>
        )}
        {report.filters.includes('po_no') && (
          <label className="text-xs">
            <span className="block font-semibold text-slate-600 mb-1">{FILTER_LABELS.po_no}</span>
            <input value={filters.po_no || ''} onChange={e => set('po_no', e.target.value)}
              placeholder="PO/2526/…"
              className="border border-slate-300 rounded px-2.5 py-1.5 text-xs w-32 focus:outline-none focus:border-[#3b4872]"
              data-testid="filter-po-no" />
          </label>
        )}
        {report.filters.includes('include_cancelled') && (
          <label className="text-xs flex items-center gap-1.5 pb-1.5 cursor-pointer select-none">
            <input type="checkbox" checked={!!filters.include_cancelled}
              onChange={e => set('include_cancelled', e.target.checked)}
              className="accent-[#3b4872]" data-testid="filter-include-cancelled" />
            <span className="font-semibold text-slate-600">{FILTER_LABELS.include_cancelled}</span>
          </label>
        )}
        <div className="flex gap-2 ml-auto">
          <button onClick={runPreview} disabled={loading || rangeMissing}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-display font-bold border border-[#3b4872] text-[#3b4872] rounded-sm hover:bg-[#3b4872]/5 disabled:opacity-40"
            data-testid="report-preview-btn">
            {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Preview
          </button>
          <button onClick={runExport} disabled={exporting || rangeMissing}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-display font-bold bg-[#3b4872] text-white rounded-sm hover:bg-[#2c3658] disabled:opacity-40"
            data-testid="report-export-btn">
            {exporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Export Excel
          </button>
        </div>
      </div>
      {rangeMissing && <div className="text-xs text-amber-600" data-testid="report-range-hint">Pick a From and To date to run this report.</div>}

      {/* Preview table */}
      {preview && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="report-preview-table">
          <div className="px-3 py-2 border-b border-slate-100 text-[11px] text-slate-500">
            {preview.total} row{preview.total !== 1 ? 's' : ''}{preview.total > 200 ? ' — showing first 200 (export for the full report)' : ''}
          </div>
          <table className="w-full dense-table text-xs">
            <thead>
              <tr>{report.cols.map(c => <th key={c.k} className={c.num ? 'text-right' : 'text-left'}>{c.l}</th>)}</tr>
            </thead>
            <tbody>
              {(preview.items || []).slice(0, 200).map((r, i) => (
                <tr key={i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`report-row-${i}`}>
                  {report.cols.map(c => (
                    <td key={c.k} className={`${c.num ? 'text-right tabular-nums' : ''} max-w-[220px] truncate`}>
                      {c.num ? fmt(r[c.k], c.money) : (r[c.k] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
              {(preview.items || []).length === 0 && (
                <tr><td colSpan={report.cols.length} className="text-center py-8 text-slate-400">No data for these filters</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ReportsTab;
